// Copyright (c) 2025 by Audere

import {
  AvatarGroup,
  Box,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
} from "@mui/material";
import SmallChip, { priorityChipStyles, statusChipStyles } from "./SmallChip";
import { createStyleSheet, globalStyles } from "../utils/styles";
import {
  LabelerConversationData,
  LabelerData,
  LabelingStatus,
  QCConversationData,
  QCData,
} from "../features/api/labelApiSlice";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import React, { useMemo } from "react";
import { formatTimestamp } from "../utils/dates";
import { green, grey } from "@mui/material/colors";
import { CustomAvatar } from "./CustomAvatar";
import {
  getAnnotatorsStatusGroups,
  getUserLabelStatus,
  hasInProgressLabels,
  hasSubmittedLabels,
  orderAnnotators,
} from "../features/data/annotatorStatusUtils";
import {
  formatStatusText,
  getAnnotators,
  getAnnotatorsForMonitor,
} from "../features/data/conversationUtils";
import { useCurrentUser } from "../hooks/useCurrentUser";

interface ConversationItemProps {
  conversation: LabelerConversationData | QCConversationData;
  isSelected: boolean;
  onClick: () => void;
}

const STATUS_PRIORITY_FOR_MONITOR = [
  LabelingStatus.qc_complete,
  LabelingStatus.qc_in_progress,
  LabelingStatus.label_complete,
  LabelingStatus.label_in_progress,
] as const;

const ConversationListItem: React.FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  onClick,
}) => {
  const { email, isMonitor } = useCurrentUser();
  const { conversation_uid: uid, priority } = conversation;

  const annotators = isMonitor
    ? getAnnotatorsForMonitor(conversation)
    : getAnnotators(conversation);
  const priorityAnnotators = useMemo<(LabelerData | QCData)[]>(() => {
    if (!isMonitor) return [];

    for (const status of STATUS_PRIORITY_FOR_MONITOR) {
      const annotatorsByStatus = getAnnotatorsStatusGroups(annotators, status);
      if (annotatorsByStatus.length) {
        return annotatorsByStatus;
      }
    }

    return [];
  }, [annotators, isMonitor]);
  const sortedAnnotators = useMemo(
    () => orderAnnotators(isMonitor ? priorityAnnotators : annotators, email),
    [isMonitor, priorityAnnotators, annotators, email]
  );
  const labelingStatus = isMonitor
    ? priorityAnnotators?.length
      ? getUserLabelStatus(priorityAnnotators, priorityAnnotators[0].email)
      : undefined
    : getUserLabelStatus(annotators, email);

  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={onClick}
        sx={{
          ...(isSelected
            ? globalStyles.selectedStyle
            : globalStyles.unselectedStyle),
          flexWrap: "wrap",
        }}
        aria-label={`Conversation ${uid}, priority ${priority}`}
      >
        <Box sx={styles.itemText}>
          <ListItemText
            primary={uid}
            secondary={formatTimestamp(conversation.start_timestamp)}
          />
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
            {isMonitor ? (
              labelingStatus && (
                <SmallChip
                  key={labelingStatus}
                  label={formatStatusText(labelingStatus)}
                  sx={
                    statusChipStyles[
                      labelingStatus as keyof typeof statusChipStyles
                    ]
                  }
                />
              )
            ) : (
              <>
                {hasSubmittedLabels(labelingStatus) && (
                  <SmallChip label="Complete" sx={styles.completeChip} />
                )}
                {hasInProgressLabels(labelingStatus) && (
                  <SmallChip label="In progress" sx={styles.inProgressChip} />
                )}
              </>
            )}
            <SmallChip label={priority} sx={priorityChipStyles[priority]} />
          </Stack>
        </Box>
        <AvatarGroup
          max={4}
          aria-label={`Annotators for ${uid}`}
          sx={styles.avatarGroup}
        >
          {sortedAnnotators.map((annotator) => {
            return <CustomAvatar key={annotator.email} user={annotator} />;
          })}
        </AvatarGroup>
      </ListItemButton>
      <NavigateNextIcon aria-label="view conversation" />
    </ListItem>
  );
};

const styles = createStyleSheet({
  inProgressChip: {
    border: `1px solid ${grey[800]}`,
    color: grey[800],
    fontWeight: "bold",
  },
  completeChip: {
    border: `1px solid ${green[800]}`,
    backgroundColor: green[100],
    color: green[900],
    fontWeight: "bold",
  },
  itemText: {
    width: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },
  avatarGroup: {
    "& .MuiAvatar-root": {
      width: 24,
      height: 24,
      fontSize: "0.75rem",
    },
    paddingY: 0.5,
  },
});

export default ConversationListItem;
